import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { SystemNode, SystemNodeKind } from '@/constants/case-studies'

import { useRef } from 'react'
import {
    CircleCheck,
    Database,
    Sparkles,
    UserCheck,
    Zap
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface SystemDiagramProps {
    nodes: SystemNode[]
}

const KINDS: Record<SystemNodeKind, { label: string, icon: LucideIcon, className: string }> = {
    trigger: { label: 'Trigger', icon: Zap, className: 'border-line-strong text-soft' },
    agent: { label: 'AI agent', icon: Sparkles, className: 'border-ember/50 text-ember' },
    system: { label: 'System', icon: Database, className: 'border-line-strong text-soft' },
    human: { label: 'Human', icon: UserCheck, className: 'border-foreground/30 text-foreground' },
    action: { label: 'Action', icon: CircleCheck, className: 'border-line-strong text-soft' }
}

export const SystemDiagram: FC<SystemDiagramProps> = ({ nodes }): ReactNode => {
    const ref = useRef<HTMLOListElement>(null)
    const inView = useInView(ref)

    return (
        <div>
            <ol ref={ref} className='grid gap-4 md:grid-cols-3 xl:grid-cols-6' aria-label='Automation system, step by step'>
                {nodes.map((node, index) => {
                    const kind = KINDS[node.kind]
                    const isLast = index === nodes.length - 1
                    const endOfRowMd = (index + 1) % 3 === 0

                    return (
                        <li
                            key={`${node.label}-${index}`}
                            className={cn(
                                'relative flex flex-col gap-6 border bg-surface p-5 transition-[opacity,transform] duration-700 ease-expo',
                                node.kind === 'agent' ? 'border-ember/35' : 'border-line-strong',
                                inView ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                            )}
                            style={{ transitionDelay: inView ? `${index * 130}ms` : '0ms' }}
                        >
                            {!isLast && (
                                <>
                                    {/* Vertical connector on small screens */}
                                    <span aria-hidden='true' className='absolute -bottom-4 left-8 h-4 w-px overflow-hidden bg-line-strong md:hidden'>
                                        <span className='flow-down absolute inset-x-0 top-0 h-2 bg-ember' />
                                    </span>

                                    {/* Horizontal connector on wider screens */}
                                    <span
                                        aria-hidden='true'
                                        className={cn(
                                            'absolute top-1/2 -right-4 hidden h-px w-4 bg-ember/70 md:block',
                                            endOfRowMd && 'md:hidden xl:block'
                                        )}
                                    />
                                </>
                            )}

                            <div className='flex items-center justify-between gap-3'>
                                <span className='type-label text-muted-foreground'>
                                    0{index + 1}
                                </span>

                                <span className={cn('type-label flex items-center gap-1.5 border px-2 py-1', kind.className)}>
                                    <kind.icon className='size-3' />
                                    {kind.label}
                                </span>
                            </div>

                            <div className='mt-auto'>
                                <p className='font-heading text-[1.15rem] leading-snug tracking-[-0.02em] text-foreground'>
                                    {node.label}
                                </p>

                                <p className='mt-1.5 text-sm leading-relaxed text-soft'>
                                    {node.detail}
                                </p>
                            </div>
                        </li>
                    )
                })}
            </ol>

            <div className='mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line pt-5'>
                {(Object.keys(KINDS) as SystemNodeKind[]).map((key) => {
                    const kind = KINDS[key]

                    return (
                        <span key={key} className='type-label flex items-center gap-2 text-muted-foreground'>
                            <kind.icon className={cn('size-3.5', key === 'agent' ? 'text-ember' : 'text-soft')} />
                            {kind.label}
                        </span>
                    )
                })}
            </div>
        </div>
    )
}
